import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getCompliance } from '@/api/clarityLens'
import { ScalrTableColumn } from '@/components/customComponents'
import { pushNotification, useAppDispatch } from '@/store'
import { areFilterParamsValid } from '@/utils/filterParams'
import {
  ComplianceModel,
  CompliancePercentageResponseModel,
  StoreComplianceDetailsModel,
  StoreComplianceDetailsResponseModel,
  StoreLeakageResponseModel,
} from '@/types/clarityLens'

type ChartDataModel = {
  data: Record<string, number>
  labels: string[]
}

type LineChartDataModel = {
  rawData: number[][]
  seriesNames: string[]
  xAxisNames: string[]
}

type BarChartDataModel = {
  name: string
  value: number
}[]

const storeComplianceDetailsColumns: ScalrTableColumn[] = [
  {
    header: 'Store Name',
    accessorKey: 'storeName',
    type: 'text',
  },
  {
    header: 'Compliance Percentage',
    accessorKey: 'compliancePercentage',
    type: 'percentage',
    isSortable: true,
  },
  {
    header: 'Non-Compliant Spend',
    accessorKey: 'nonCompliantSpend',
    type: 'money',
    isSortable: true,
  },
  {
    header: 'Majority Supplier',
    accessorKey: 'majoritySupplier',
    type: 'text',
  },
  {
    header: 'Last Breach Date',
    accessorKey: 'lastBreachDate',
    type: 'text',
  },
]

export default function useCompliance(
  startDate: string,
  endDate: string,
  branches: string,
  itemName: string
) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [isFetching, setIsFetching] = useState<boolean>(false)

  const isReady = useMemo(
    () =>
      areFilterParamsValid(startDate, endDate, branches) &&
      Boolean(itemName?.trim()),
    [startDate, endDate, branches, itemName]
  )
  const loading = !isReady || isFetching

  const [compliancePercentageChartData, setCompliancePercentageChartData] =
    useState<ChartDataModel>()
  const [monthlyComplianceChartData, setMonthlyComplianceChartData] =
    useState<LineChartDataModel>()
  const [storeLeakageChartData, setStoreLeakageChartData] =
    useState<BarChartDataModel>()
  const [storeComplianceDetails, setStoreComplianceDetails] =
    useState<StoreComplianceDetailsModel[]>()

  const fetchAllData = useCallback(async () => {
    if (!isReady) {
      setIsFetching(false)
      return
    }
    setIsFetching(true)
    try {
      const { data } = await fetchComplianceData(
        branches,
        startDate,
        endDate,
        itemName
      )

      const compliancePercentageChartData: ChartDataModel = {
        data: {
          Compliant: data.compliancePercentage.compliant,
          'Non-Compliant': data.compliancePercentage.nonCompliant,
        },
        labels: ['Compliant', 'Non-Compliant'],
      }

      const validMonthlyKeys = Object.keys(data.monthlyCompliance).filter(
        (key) => data.monthlyCompliance[key] !== null
      )
      const compliantSeries = validMonthlyKeys.map(
        (key) => data.monthlyCompliance[key]?.compliant ?? 0
      )
      const nonCompliantSeries = validMonthlyKeys.map(
        (key) => data.monthlyCompliance[key]?.nonCompliant ?? 0
      )
      const monthlyComplianceChartData: LineChartDataModel = {
        rawData: [compliantSeries, nonCompliantSeries],
        seriesNames: ['Compliant', 'Non-Compliant'],
        xAxisNames: validMonthlyKeys.map(
          (key) => key.charAt(0).toUpperCase() + key.slice(1)
        ),
      }

      const storeLeakageChartData: BarChartDataModel = data.storeLeakage.map(
        (item) => ({
          name: item.dcn,
          value: item.leakage,
        })
      )

      setCompliancePercentageChartData(compliancePercentageChartData)
      setMonthlyComplianceChartData(monthlyComplianceChartData)
      setStoreLeakageChartData(storeLeakageChartData)
      setStoreComplianceDetails(data.storeComplianceDetails)
    } catch (err) {
      const errorInstance = new Error(
        err instanceof Error ? err.message : 'An unknown error occurred'
      )

      dispatch(
        pushNotification({
          type: 'error',
          title: t('common.errors.serverError'),
          description: errorInstance.message,
          duration: 5000,
        })
      )
    } finally {
      setIsFetching(false)
    }
  }, [isReady, branches, startDate, endDate, itemName])

  const refetch = useCallback(() => {
    console.log('Refetching compliance data...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    compliancePercentageChartData,
    monthlyComplianceChartData,
    storeLeakageChartData,
    storeComplianceDetails,
    storeComplianceDetailsColumns,
    loading,
    refetch,
  }
}

async function fetchComplianceData(
  branches: string,
  startDate: string,
  endDate: string,
  itemName: string
): Promise<{
  data: ComplianceModel
}> {
  try {
    const response = await getCompliance(branches, startDate, endDate, itemName)

    const data: ComplianceModel = convertData(
      response.compliance_percentage,
      response.monthly_compliance,
      response.store_leakage,
      response.store_compliance_details
    )

    return {
      data,
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}

function convertData(
  compliancePercentage: CompliancePercentageResponseModel,
  monthlyCompliance: {
    [key: string]: CompliancePercentageResponseModel | null
  },
  storeLeakage: StoreLeakageResponseModel[],
  storeComplianceDetails: StoreComplianceDetailsResponseModel[]
): ComplianceModel {
  if (!monthlyCompliance || typeof monthlyCompliance !== 'object') {
    throw new Error('Monthly Compliance is invalid')
  }

  return {
    compliancePercentage: {
      compliant: compliancePercentage.compliant,
      nonCompliant: compliancePercentage.non_compliant,
    },
    monthlyCompliance: Object.fromEntries(
      Object.entries(monthlyCompliance).map(([key, value]) => {
        if (value === null) {
          return [key.charAt(0).toUpperCase() + key.slice(1), null]
        }

        return [
          key.charAt(0).toUpperCase() + key.slice(1),
          {
            compliant: value.compliant,
            nonCompliant: value.non_compliant,
          },
        ]
      })
    ),
    storeLeakage: storeLeakage,
    storeComplianceDetails: storeComplianceDetails.map((item) => ({
      storeName: item.store_name,
      compliancePercentage: item.compliance_percentage,
      nonCompliantSpend: item.non_compliant_spend,
      majoritySupplier: item.majority_supplier,
      lastBreachDate: item.last_breach_date ?? '-',
    })),
  }
}
